<template>
  <BlockFieldset class="project-bank-rental-income">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <div v-else>
      <div class="bank-section-grid bank-section-grid--paired">
        <div class="label">{{ $t('bankHasRentalIncome') }}</div>
        <div>
          <b-form-select
            id="bankHasRentalIncome"
            :value="project.bank.hasRentalIncome"
            @change="setBankField('hasRentalIncome', $event)"
            :options="yesNoOptions"
          />
        </div>
        <template v-for="row in ATTACHMENT_ROWS">
          <div class="label" :key="`${row.attachmentType}-label`">{{ $t(row.labelKey) }}</div>
          <div :key="row.attachmentType">
            <BankAttachmentUpload
              :project="project"
              :attachmentType="row.attachmentType"
              @changed="reloadBlock"
            />
          </div>
        </template>
      </div>
      <div v-if="!isBlockReadOnly" class="text-right mt-2">
        <b-button variant="bg" :disabled="isSaving" @click="save">
          {{ $t('saveNamed', { name: $t('bankSectionRentalIncome') }) }}
        </b-button>
      </div>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapActions } from 'vuex';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import BankAttachmentUpload from '@/components/Projects/BankAttachmentUpload';
import { BANK_ATTACHMENT_TYPES } from '@/helpers/bankAttachmentTypes';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';
import { ProjectBankBlockMixin } from '@/mixins/projectBankBlock';
import { PROJECT_BANK_RENTAL_INCOME_FIELDS } from './projectBankRentalIncomeFragment';
import { toProjectBankRentalIncomeInput } from './projectBankRentalIncomeInput';

/** Documents of the section in the order of the previous tab. */
const ATTACHMENT_ROWS = [
  {
    labelKey: 'bankAttachmentRentalIncomeStatements',
    attachmentType: BANK_ATTACHMENT_TYPES.BANK_RENTAL_INCOME_STATEMENTS,
  },
];

/**
 * Block project-bank-rental-income ("Mieteinnahmen", tab Bank, see feature page projekt-bank): whether there is rental income, saved with the own button, and the documents of the section.
 * `project` is the block data (`Project.blocks.bankRentalIncome`); an upload or a delete reloads it. On
 * the unsaved project it shows only a hint. Emits `dirty` and `saved`.
 */
export default {
  name: 'ProjectBankRentalIncome',
  components: { BankAttachmentUpload, BlockFieldset },
  mixins: [AccessBlockMixin, BlockSaveMixin, ProjectBankBlockMixin],
  bankBlockField: 'bankRentalIncome',
  bankBlockFragment: PROJECT_BANK_RENTAL_INCOME_FIELDS,
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
  },
  data() {
    return { ATTACHMENT_ROWS };
  },
  computed: {
    blockInput() {
      return toProjectBankRentalIncomeInput(this.project);
    },
    yesNoOptions() {
      return [
        { value: null, text: '' },
        { value: 'YES', text: this.$t('YES') },
        { value: 'NO', text: this.$t('NO') },
      ];
    },
  },
  methods: {
    ...mapActions(['updateProjectBankRentalIncome']),
    saveBlockInput(input) {
      return this.updateProjectBankRentalIncome({ projectNumber: this.project.number, input });
    },
    setBankField(field, value) {
      this.$set(this.project.bank, field, value);
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../styles/bankSectionGrid';
</style>
